/**
 * CURATION-F3b — shared version / colorway labelling for client + /api/style-favourites.
 * Numbering must match overlayVersionHistory (versions newest-first; colorways oldest-first per parent).
 */

export type CurationJobLike = {
  id: string;
  pipeline?: string | null;
  status?: string | null;
  created_at?: string | null;
  prompt?: string | null;
  payload?: Record<string, unknown> | null;
};

/** Include side jobs in version history (exclude colorways). Same rule as former studioHelpers. */
export function isVersionHistorySideJob(job: CurationJobLike): boolean {
  if (job.pipeline !== "2d_side" || job.status !== "done") return false;
  const origin = job.payload?.job_origin;
  if (origin === "colorway") return false;
  if (origin === "side_master" || origin === "side_regen") return true;
  // Pre-marker fallback: non-empty prompt excludes colorways (stored as null)
  const prompt = job.prompt;
  if (typeof prompt === "string" && prompt.trim().length > 0) return true;
  return false;
}

/**
 * Prefers payload.colorway_name; otherwise "Colorway {n}" (1-based under parent).
 */
export function colorwayDisplayLabel(
  payload: Record<string, unknown> | null | undefined,
  index1Based: number,
): string {
  const name =
    typeof payload?.colorway_name === "string"
      ? payload.colorway_name.trim()
      : "";
  if (name) return name;
  return `Colorway ${index1Based}`;
}

function createdAtMs(job: CurationJobLike): number {
  if (!job.created_at) return 0;
  const t = new Date(job.created_at).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Version labels over ALL version-history jobs (hidden included), newest-first → V{n}…V1.
 */
export function versionLabelsNewestFirst(
  jobs: ReadonlyArray<CurationJobLike>,
): Map<string, string> {
  const versions = jobs
    .filter((j) => isVersionHistorySideJob(j))
    .sort((a, b) => createdAtMs(b) - createdAtMs(a));
  const map = new Map<string, string>();
  for (let i = 0; i < versions.length; i++) {
    map.set(versions[i]!.id, `V${versions.length - i}`);
  }
  return map;
}

function parentJobIdOf(job: CurationJobLike): string | null {
  const raw = job.payload?.parent_job_id;
  if (typeof raw !== "string" || !raw.trim()) return null;
  return raw.trim();
}

function isColourwaySideJob(job: CurationJobLike): boolean {
  if (job.pipeline !== "2d_side" || job.status !== "done") return false;
  return job.payload?.job_origin === "colorway";
}

/**
 * Colorway labels oldest-first per parent → { label, parentJobId }.
 */
export function colorwayLabelsOldestFirstPerParent(
  jobs: ReadonlyArray<CurationJobLike>,
): Map<string, { label: string; parentJobId: string }> {
  const colourways = jobs
    .filter((j) => isColourwaySideJob(j))
    .sort((a, b) => createdAtMs(a) - createdAtMs(b));
  const parentIndex = new Map<string, number>();
  const map = new Map<string, { label: string; parentJobId: string }>();
  for (const job of colourways) {
    const parentJobId = parentJobIdOf(job);
    if (!parentJobId) continue;
    const n = (parentIndex.get(parentJobId) ?? 0) + 1;
    parentIndex.set(parentJobId, n);
    map.set(job.id, {
      label: colorwayDisplayLabel(job.payload ?? null, n),
      parentJobId,
    });
  }
  return map;
}

/** Untitled / empty → "Untitled" (same display as client folderLabel). */
export function collectionDisplayName(name: string | null | undefined): string {
  const raw = name?.trim() ?? "";
  if (!raw || /^untitled(\s+collection)?$/i.test(raw)) return "Untitled";
  return raw;
}
